import { Brand50, Brand60, Neutral500, ScenarioDeltaGreen100 } from '@src/utils/colors';

// Bliss Point Creative Suite v3 — a design mock reproduced faithfully with the
// sample data baked into the original HTML. Everything below is representative
// demo data, not a live feed.

// --- Palette --------------------------------------------------------------
// The mock's vivid status colors have no exact equivalent in colors.ts (its
// orange/red/green are more saturated than $Warning/$Danger/$Success), so we
// pin the literals here — confined to this block — to match the design 1:1.
export const CsOrange = '#ff8200';
export const CsRed = '#e24b4a';
export const CsRedText = '#a32d2d'; // darker red used for status text/labels
export const CsGreen = ScenarioDeltaGreen100;

// --- Internal tabs --------------------------------------------------------
export const SuiteTab = {
  COMBINED: 'combined',
  DIVERSITY: 'diversity',
  FATIGUE: 'fatigue'
} as const;
export type SuiteTabType = (typeof SuiteTab)[keyof typeof SuiteTab];

// --- Shared value tones ---------------------------------------------------
export const Tone = {
  DEFAULT: 'default',
  UP: 'up',
  DOWN: 'down',
  WARN: 'warn'
} as const;
export type ToneType = (typeof Tone)[keyof typeof Tone];

export interface MetricCard {
  label: string;
  value: string; // rendered text (may be a chip via `chip`)
  valueTone?: ToneType;
  valueSuffix?: string; // small muted trailing unit e.g. "/wk"
  sub: string;
  subTone?: ToneType;
  chip?: 'low'; // render the value as a diversity label chip instead of text
}

// --- Fatigue threshold ----------------------------------------------------
export const FATIGUE_THRESHOLD = 0.8;

// --- "Fatigue over time · by ad set" line chart ---------------------------
export const TIMELINE_DAYS = Array.from({ length: 30 }, (_, i) => `D${i + 1}`);

export interface SetLine {
  key: string;
  label: string;
  color: string;
  values: number[]; // one per TIMELINE_DAYS entry
  launchDayIndex: number; // index into TIMELINE_DAYS where a new ad went live
}

export const SET_LINES: SetLine[] = [
  {
    key: 'fitness',
    label: 'Prospecting — Fitness',
    color: CsRed,
    launchDayIndex: 9,
    values: [
      0.3, 0.32, 0.34, 0.33, 0.37, 0.4, 0.42, 0.45, 0.48, 0.5, 0.53, 0.55, 0.58, 0.6, 0.63, 0.66,
      0.68, 0.7, 0.72, 0.74, 0.76, 0.78, 0.8, 0.82, 0.83, 0.84, 0.85, 0.86, 0.86, 0.86
    ]
  },
  {
    key: 'cart',
    label: 'Retargeting — Cart 7d',
    color: CsOrange,
    launchDayIndex: 21,
    values: [
      0.25, 0.27, 0.28, 0.3, 0.33, 0.34, 0.37, 0.4, 0.41, 0.44, 0.46, 0.43, 0.45, 0.48, 0.5, 0.53,
      0.55, 0.57, 0.6, 0.62, 0.64, 0.67, 0.69, 0.71, 0.73, 0.74, 0.76, 0.78, 0.8, 0.81
    ]
  },
  {
    key: 'lookalike',
    label: 'Lookalike 3%',
    color: Brand50,
    launchDayIndex: 14,
    values: [
      0.35, 0.36, 0.38, 0.4, 0.42, 0.43, 0.45, 0.44, 0.47, 0.49, 0.51, 0.53, 0.55, 0.54, 0.57, 0.59,
      0.61, 0.63, 0.62, 0.65, 0.67, 0.69, 0.7, 0.72, 0.71, 0.73, 0.74, 0.75, 0.76, 0.77
    ]
  }
];

export const HEALTHY_HIDDEN_COUNT = 12;

// --- Focus (isolated ad set) dual-axis chart ------------------------------
export const FOCUS_SET_LABEL = 'Prospecting — Fitness';

export interface FocusLine {
  key: string;
  label: string;
  color: string;
  axis: 'score' | 'perf';
  dashed?: boolean;
  values: number[];
}

export const FOCUS_LINES: FocusLine[] = [
  {
    key: 'fatigue',
    label: 'Fatigue',
    color: CsRed,
    axis: 'score',
    values: [
      0.3, 0.32, 0.34, 0.33, 0.37, 0.4, 0.42, 0.45, 0.48, 0.5, 0.53, 0.55, 0.58, 0.6, 0.63, 0.66,
      0.68, 0.7, 0.72, 0.74, 0.76, 0.78, 0.8, 0.82, 0.83, 0.84, 0.85, 0.86, 0.86, 0.86
    ]
  },
  {
    key: 'diversification',
    label: 'Diversification',
    color: Brand60,
    axis: 'score',
    dashed: true,
    values: [
      0.55, 0.54, 0.54, 0.52, 0.51, 0.5, 0.49, 0.48, 0.47, 0.46, 0.45, 0.44, 0.43, 0.42, 0.41, 0.4,
      0.39, 0.38, 0.37, 0.36, 0.35, 0.34, 0.33, 0.33, 0.32, 0.31, 0.3, 0.29, 0.29, 0.28
    ]
  },
  {
    key: 'roas',
    label: 'ROAS',
    color: CsGreen,
    axis: 'perf',
    values: [
      3.1, 3.0, 3.1, 2.9, 2.9, 2.8, 2.7, 2.7, 2.6, 2.5, 2.5, 2.4, 2.3, 2.3, 2.2, 2.2, 2.1, 2.0, 2.0,
      1.9, 1.9, 1.9, 1.8, 1.8, 1.8, 1.8, 1.8, 1.8, 1.8, 1.8
    ]
  },
  {
    key: 'cpa',
    label: 'CPA',
    color: Neutral500,
    axis: 'perf',
    values: [
      34, 35, 34, 36, 37, 38, 39, 40, 41, 43, 44, 45, 47, 48, 49, 50, 52, 53, 54, 55, 56, 57, 58,
      58, 58, 58, 58, 58, 58, 58
    ]
  }
];

export const FOCUS_PERF_MAX = 60;

// --- Fatigue × Diversity quadrant bubble chart ----------------------------
export interface QuadrantBubble {
  x: number; // diversification 0–1
  y: number; // fatigue 0–1
  r: number; // relative spend (bubble radius hint)
}

export interface BubbleGroup {
  key: string;
  color: string;
  bubbles: QuadrantBubble[];
}

export const QUADRANT_GROUPS: BubbleGroup[] = [
  {
    key: 'healthy',
    color: CsGreen,
    bubbles: [
      { x: 0.74, y: 0.22, r: 13 },
      { x: 0.81, y: 0.15, r: 9 },
      { x: 0.68, y: 0.31, r: 16 },
      { x: 0.88, y: 0.27, r: 7 },
      { x: 0.62, y: 0.19, r: 11 }
    ]
  },
  {
    key: 'watch',
    color: CsOrange,
    bubbles: [
      { x: 0.4, y: 0.58, r: 14 },
      { x: 0.52, y: 0.49, r: 10 },
      { x: 0.33, y: 0.44, r: 18 },
      { x: 0.58, y: 0.62, r: 8 },
      { x: 0.46, y: 0.4, r: 12 }
    ]
  },
  {
    key: 'flagged',
    color: CsRed,
    bubbles: [
      { x: 0.18, y: 0.86, r: 22 },
      { x: 0.31, y: 0.78, r: 17 },
      { x: 0.24, y: 0.71, r: 13 },
      { x: 0.12, y: 0.81, r: 10 }
    ]
  }
];

// Bubble color mode: the mock toggles between coloring bubbles by ROAS health
// (the default group colors) and by campaign (recolored from the brand palette).
export const BubbleColorMode = {
  ROAS: 'roas',
  CAMPAIGN: 'campaign'
} as const;
export type BubbleColorModeType = (typeof BubbleColorMode)[keyof typeof BubbleColorMode];

export const CAMPAIGN_BUBBLE_COLORS = [Brand60, Brand50, Neutral500];

export const QUADRANT_MIDPOINT = 0.5;
export const PLAYBACK_WEEK_LABEL = 'Week 3 of 5';
export const PLAYBACK_DATE_LABEL = 'May 17';
export const PLAYBACK_PROGRESS = 0.62; // scrubber fill fraction

// --- Creative thumbnails --------------------------------------------------
export const CreativeKind = {
  VIDEO: 'video',
  PHOTO: 'photo',
  CAROUSEL: 'carousel'
} as const;
export type CreativeKindType = (typeof CreativeKind)[keyof typeof CreativeKind];

// --- Diversity tab: low-diversity / high-spend table ----------------------
export type DiversityRisk = 'red' | 'amber';

export interface DiversityRow {
  adset: string;
  concentrationTag: string; // e.g. "1 creative = 91% spend"
  risk: DiversityRisk;
  diversity: string; // formatted score
  spend: string;
  roas: string;
  ctr: string;
  cpa: string;
  creatives: CreativeKindType[];
}

export const DIVERSITY_ROWS: DiversityRow[] = [
  {
    adset: 'Prospecting — Broad US',
    concentrationTag: '1 creative = 91% spend',
    risk: 'red',
    diversity: '0.18',
    spend: '$62.4K',
    roas: '2.1×',
    ctr: '0.8%',
    cpa: '$41',
    creatives: [CreativeKind.PHOTO]
  },
  {
    adset: 'Retargeting — Cart 7d',
    concentrationTag: '2 creatives = 84% spend',
    risk: 'amber',
    diversity: '0.31',
    spend: '$44.0K',
    roas: '4.8×',
    ctr: '1.9%',
    cpa: '$19',
    creatives: [CreativeKind.VIDEO, CreativeKind.PHOTO]
  },
  {
    adset: 'Lookalike 3% — Purchase',
    concentrationTag: '3 creatives = 79% spend',
    risk: 'amber',
    diversity: '0.38',
    spend: '$38.1K',
    roas: '3.0×',
    ctr: '1.3%',
    cpa: '$33',
    creatives: [CreativeKind.PHOTO, CreativeKind.CAROUSEL, CreativeKind.VIDEO]
  }
];

export interface Recommendation {
  title: string;
  body: string;
}

export const DIVERSITY_RECS: Recommendation[] = [
  {
    title: 'Prospecting — Broad US',
    body: 'One creative drives 91% of spend. Add 2–3 new creatives to clear the 0.55 diversity floor.'
  },
  {
    title: 'Retargeting — Cart 7d',
    body: 'Two creatives carry 84% of spend. Add 2 more to reduce concentration.'
  }
];

// --- Fatigue tab: drill-down ----------------------------------------------
export interface FatigueSetHeader {
  adset: string;
  campaign: string;
  fatigue: string;
  frequency: string;
  spend: string;
  cpa: string;
  cpaInflation: string;
}

export const FATIGUE_SET: FatigueSetHeader = {
  adset: 'Prospecting — Interest: Fitness',
  campaign: 'Q2 Prospecting',
  fatigue: '0.86',
  frequency: '6.4',
  spend: '$51.2K',
  cpa: '$58',
  cpaInflation: '+31%'
};

export interface FatigueAd {
  name: string;
  format: string; // "15s video", "static", "4 cards"
  kind: CreativeKindType;
  adset: string;
  liveDays: number;
  fatigue: number; // 0–1, drives the bar width
  frequency: string;
  ctr: string;
  ctrTone: ToneType;
  cpa: string;
}

export const FATIGUE_ADS: FatigueAd[] = [
  {
    name: '"Summer Shred"',
    format: '15s video',
    kind: CreativeKind.VIDEO,
    adset: 'Prospecting — Fitness',
    liveDays: 41,
    fatigue: 0.93,
    frequency: '7.8',
    ctr: '0.6%',
    ctrTone: Tone.WARN,
    cpa: '$61'
  },
  {
    name: '"Before / After"',
    format: 'static',
    kind: CreativeKind.PHOTO,
    adset: 'Prospecting — Fitness',
    liveDays: 38,
    fatigue: 0.81,
    frequency: '5.9',
    ctr: '0.9%',
    ctrTone: Tone.WARN,
    cpa: '$47'
  },
  {
    name: '"Gear Carousel"',
    format: '4 cards',
    kind: CreativeKind.CAROUSEL,
    adset: 'Prospecting — Fitness',
    liveDays: 22,
    fatigue: 0.44,
    frequency: '3.1',
    ctr: '1.7%',
    ctrTone: Tone.UP,
    cpa: '$29'
  }
];

export const FATIGUE_RECS: Recommendation[] = [
  {
    title: 'Prospecting — Fitness · "Summer Shred" + "Before / After"',
    body: 'Both ads are above 0.80 fatigue. Pause them and shift budget to "Gear Carousel" (0.44). Recovers ~$14K/wk.'
  }
];

// --- Creative gallery modal -----------------------------------------------
export interface GalleryCreative {
  name: string;
  kind: CreativeKindType;
  fatigue: string;
  risk: DiversityRisk | 'green';
  ctr: string;
  cpa: string;
}

export const GALLERY_TITLE = 'Prospecting — Interest: Fitness';
export const GALLERY_CREATIVES: GalleryCreative[] = [
  {
    name: '"Summer Shred"',
    kind: CreativeKind.VIDEO,
    fatigue: '0.93',
    risk: 'red',
    ctr: '0.6%',
    cpa: '$61'
  },
  {
    name: '"Before / After"',
    kind: CreativeKind.PHOTO,
    fatigue: '0.81',
    risk: 'amber',
    ctr: '0.9%',
    cpa: '$47'
  },
  {
    name: '"Gear Carousel"',
    kind: CreativeKind.CAROUSEL,
    fatigue: '0.44',
    risk: 'green',
    ctr: '1.7%',
    cpa: '$29'
  }
];

// --- Top-of-tab context (topbar + alert) ----------------------------------
export const SUITE_CLIENT = 'Northwind Apparel';
export const SUITE_PRESET = 'Last 30 days';
export const SUITE_KPI = 'Purchases / ROAS';
export const SUITE_FILTER_COUNT = 2;

export const COMBINED_METRICS: MetricCard[] = [
  { label: 'Account diversity', value: 'LOW', chip: 'low', sub: '0.41 · target ≥0.55' },
  {
    label: 'Fatigued spend',
    value: '$31K',
    valueTone: Tone.WARN,
    valueSuffix: '/wk',
    sub: 'on fatigued ads'
  },
  { label: 'CPA inflation', value: '+22%', valueTone: Tone.DOWN, sub: 'flagged sets vs baseline' },
  { label: 'ROAS', value: '3.4×', sub: '−0.5 in flagged sets', subTone: Tone.DOWN }
];

export const DIVERSITY_METRICS: MetricCard[] = [
  { label: 'Low-diversity / high-spend', value: '6', valueTone: Tone.WARN, sub: '$188K combined' },
  { label: 'Account label', value: 'LOW', chip: 'low', sub: '0.41 · was 0.57' },
  { label: 'CPA inflation', value: '+18%', valueTone: Tone.DOWN, sub: 'low-diversity sets' }
];

export const FATIGUE_METRICS: MetricCard[] = [
  { label: 'Ad sets > 0.80', value: '3', valueTone: Tone.WARN, sub: '$167K exposed' },
  { label: 'Fatigued ads', value: '23', sub: 'freq > 4.0' },
  {
    label: 'Fatigued spend',
    value: '$31K',
    valueTone: Tone.WARN,
    valueSuffix: '/wk',
    sub: 'recoverable'
  },
  { label: 'CPA inflation', value: '+31%', valueTone: Tone.DOWN, sub: 'vs first-week baseline' }
];
